/**
 * Desk Preset Editor — create, load, delete, import/export custom desk presets.
 * Custom presets are stored in localStorage. Desktop: Tauri secure store.
 * Renders as a section appended to the panel picker dropdown.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { DESK_PRESETS, getDeskById, type DeskPreset } from '@/config/desk-presets';
import { computeEnabledPanels } from '@/ui/components/panel-picker';

const CUSTOM_PRESETS_KEY = 'sentinel-custom-presets';

// ── Persistence ──

export function loadCustomPresets(): DeskPreset[] {
  try {
    const raw = localStorage.getItem(CUSTOM_PRESETS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as DeskPreset[];
  } catch { return []; }
}

function saveCustomPresets(presets: DeskPreset[]): void {
  try {
    localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(presets));
  } catch { /* ignore */ }
}

export function getCustomPresetById(id: string): DeskPreset | undefined {
  return loadCustomPresets().find(p => p.id === id);
}

export function getAllPresets(): DeskPreset[] {
  return [...DESK_PRESETS, ...loadCustomPresets()];
}

export function getAnyPresetById(id: string): DeskPreset | undefined {
  return getDeskById(id) ?? getCustomPresetById(id);
}

// ── Capture current configuration as a preset ──

function captureCurrentConfig(name: string): DeskPreset {
  const state = appStore.getState();
  const deskId = state.variant;
  const enabledPanels = Array.from(computeEnabledPanels(deskId));

  // Derive feed categories from the current desk or use default
  const currentDesk = getAnyPresetById(deskId);
  const feedCategories = currentDesk?.feedCategories ?? ['breaking'];
  const layerGroupOrder = currentDesk?.layerGroupOrder ?? ['conflicts'];

  const id = `custom-${Date.now()}`;
  return {
    id,
    name,
    icon: '\u2B50', // star
    description: `Custom preset based on ${currentDesk?.name ?? deskId}`,
    defaultLayers: [...state.activeLayers],
    enabledPanels,
    feedCategories: [...feedCategories],
    layerGroupOrder: [...layerGroupOrder],
  };
}

// ── Export / Import ──

function exportPresetJSON(preset: DeskPreset): void {
  const json = JSON.stringify(preset, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sentinel-preset-${preset.id}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function importPresetJSON(): Promise<DeskPreset | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { resolve(null); return; }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const preset = JSON.parse(reader.result as string) as DeskPreset;
          if (!preset.id || !preset.name || !preset.defaultLayers) {
            console.warn('[Sentinel] Invalid preset file');
            resolve(null);
            return;
          }
          // Ensure unique ID
          preset.id = `custom-${Date.now()}`;
          resolve(preset);
        } catch {
          console.warn('[Sentinel] Failed to parse preset JSON');
          resolve(null);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  });
}

// ── UI ──

export function createDeskPresetEditor(): { element: HTMLElement; refresh: () => void; destroy: () => void } {
  const section = document.createElement('div');
  section.className = 'preset-editor-section';

  function render(): void {
    section.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'preset-editor-title';
    title.textContent = 'Custom Presets';
    section.appendChild(title);

    // "Save current as preset" button
    const saveRow = document.createElement('div');
    saveRow.className = 'preset-editor-row';

    const saveInput = document.createElement('input');
    saveInput.className = 'preset-editor-input';
    saveInput.placeholder = 'Preset name...';
    saveInput.style.flex = '1';
    saveRow.appendChild(saveInput);

    const saveBtn = document.createElement('button');
    saveBtn.className = 'preset-editor-btn';
    saveBtn.textContent = 'Save';
    saveBtn.onclick = () => {
      const name = saveInput.value.trim();
      if (!name) return;
      const preset = captureCurrentConfig(name);
      const presets = loadCustomPresets();
      presets.push(preset);
      saveCustomPresets(presets);
      saveInput.value = '';
      render();
    };
    saveRow.appendChild(saveBtn);

    section.appendChild(saveRow);

    // List custom presets
    const custom = loadCustomPresets();
    for (const preset of custom) {
      const row = document.createElement('div');
      row.className = 'preset-editor-row';

      const name = document.createElement('span');
      name.className = 'preset-editor-name';
      name.innerHTML = `${preset.icon} ${preset.name}`;
      row.appendChild(name);

      const loadBtn = document.createElement('button');
      loadBtn.className = 'preset-editor-btn';
      loadBtn.textContent = 'Load';
      loadBtn.onclick = () => {
        applyCustomPreset(preset);
      };
      row.appendChild(loadBtn);

      const exportBtn = document.createElement('button');
      exportBtn.className = 'preset-editor-btn';
      exportBtn.textContent = 'Export';
      exportBtn.onclick = () => exportPresetJSON(preset);
      row.appendChild(exportBtn);

      const delBtn = document.createElement('button');
      delBtn.className = 'preset-editor-btn danger';
      delBtn.textContent = '\u00D7';
      delBtn.title = 'Delete preset';
      delBtn.onclick = () => {
        const presets = loadCustomPresets().filter(p => p.id !== preset.id);
        saveCustomPresets(presets);
        render();
      };
      row.appendChild(delBtn);

      section.appendChild(row);
    }

    // Import button
    const importRow = document.createElement('div');
    importRow.className = 'preset-editor-row';
    importRow.style.marginTop = '4px';

    const importBtn = document.createElement('button');
    importBtn.className = 'preset-editor-btn';
    importBtn.textContent = 'Import JSON';
    importBtn.onclick = async () => {
      const preset = await importPresetJSON();
      if (preset) {
        const presets = loadCustomPresets();
        presets.push(preset);
        saveCustomPresets(presets);
        render();
      }
    };
    importRow.appendChild(importBtn);
    section.appendChild(importRow);
  }

  render();

  const unsub = eventBus.on('variant:changed', () => render());

  return {
    element: section,
    refresh: render,
    destroy: () => { unsub(); section.remove(); },
  };
}

/** Apply a custom preset — update store and emit variant:changed. */
function applyCustomPreset(preset: DeskPreset): void {
  const current = appStore.getState().variant;
  appStore.setState({
    variant: preset.id,
    activeLayers: preset.defaultLayers,
  });
  eventBus.emit('variant:changed', { from: current, to: preset.id });

  // Persist
  try { localStorage.setItem('sentinel-desk', preset.id); } catch { /* ignore */ }
}
